import { csvDownload } from '@openx/components-core/utils/csvDownload';
import type { DealOaApi } from '@openx/types/deal';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../context/DealContext';
import type { DealActionResult, DealRowActionPayload } from '../context/DealHookTypes';
import { formatDownloadDealDetails } from '../formatDownloadDealDetails';

export const csvHeaders = [
  { key: 'name', label: 'Deal Name' },
  { key: 'deal_id', label: 'Deal ID' },
  { key: 'demandPartnersList', label: 'DSPs' },
  { key: 'id', label: 'Internal OX Deal ID' },
  { key: 'audienceName', label: 'Audience' },
  { key: 'organization', label: 'Organization' },
  { key: 'currency', label: 'Currency' },
  { key: 'deal_price', label: 'Deal Floor' },
  { key: 'expected_ad_category', label: 'Expected Sensitive Category' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
];

export const useDealsDownload = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { useDemandPartnersFetch, useMultipleDealsFetch, useExpectedAdCategoryFetch, useOnBehalfOfAccountId } =
    useDealContext();

  const onBehalfOfAccountId = useOnBehalfOfAccountId();

  const { mapById: demandPartnersMap = {}, loading: demandPartnersLoading } = useDemandPartnersFetch({
    account_uid: onBehalfOfAccountId,
  });

  const { expectedAdCategoryMap, isExpectedAdCategoryLoading } = useExpectedAdCategoryFetch();

  const { fetchDeals, loading: dealsLoading } = useMultipleDealsFetch<DealOaApi>();

  const downloadDeals = useCallback(
    async (selectedDeals: DealRowActionPayload[]): Promise<DealActionResult> => {
      const dealPluralityLabel = selectedDeals.length > 1 ? t('Deals') : t('Deal');

      const { deals } = await fetchDeals(selectedDeals.map(item => item.id));

      if (!deals || deals.length !== selectedDeals.length) {
        enqueueSnackbar(
          t('There was an error downloading {dealPluralityLabel} data.', {
            dealPluralityLabel: dealPluralityLabel.toLowerCase(),
          }),
          {
            variant: 'error',
          }
        );

        return Promise.resolve({ fulfilledIds: [] });
      }

      const formatedDealsData = formatDownloadDealDetails(deals.flat(1), demandPartnersMap, expectedAdCategoryMap);

      csvDownload({
        data: formatedDealsData,
        filename: 'DealDetailsReport',
        headers: csvHeaders,
      });

      enqueueSnackbar(t('{dealPluralityLabel} downloaded successfully.', { dealPluralityLabel }), {
        variant: 'success',
      });

      return Promise.resolve({ fulfilledIds: deals.map(item => item.uid) });
    },
    [demandPartnersMap, enqueueSnackbar, expectedAdCategoryMap, fetchDeals, t]
  );

  const download = useCallback(
    (deals: DealRowActionPayload[]) => {
      return downloadDeals(deals);
    },
    [downloadDeals]
  );

  return {
    download,
    isLoading: dealsLoading || demandPartnersLoading || isExpectedAdCategoryLoading,
  };
};
